import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {EventApiModel} from '../../api/models/event.api.model';
import {ProjectViewModel} from '../../features/user/garage/project.view-model';
import {CurrentUserViewModel} from '../models/current-user.view-model';
import {MediaPostPublishService} from '../services/media-post-publish.service';
import {ResponseNotificationService} from '../services/response-notification.service';
import {TimelineComponent} from '../../shared/timeline/timeline.component';
import {FlutterMediaComposerStart} from './flutter-shell.plugin';
import {FlutterShellService} from './flutter-shell.service';
import {NativeMediaPublisher, NativeMediaPublishEvent} from './native-media-publisher.plugin';
import {environment} from '../../../environments/environment';
import {PostViewModel} from '../../shared/timeline/post.view-model';

export interface FlutterMediaComposerContext {
  projectVM?: ProjectViewModel | null;
  eventAM?: EventApiModel | null;
  timeline?: TimelineComponent | null;
  currentUser?: CurrentUserViewModel | null;
}

@Injectable({providedIn: 'root'})
export class FlutterMediaComposerService {
  private readonly nativeJobs = new Map<string, {optimistic: PostViewModel; context: FlutterMediaComposerContext; caption: string}>();

  constructor(
    private readonly flutterShell: FlutterShellService,
    private readonly publisher: MediaPostPublishService,
    private readonly notifications: ResponseNotificationService,
    private readonly translate: TranslateService,
  ) {
    void NativeMediaPublisher.addListener('jobState', (event) => this.handleNativeJob(event)).catch(() => undefined);
    void NativeMediaPublisher.resumePending().catch(() => undefined);
  }

  public get canPresent(): boolean {
    return this.flutterShell.canPresentMediaComposer;
  }

  public async open(start: FlutterMediaComposerStart, context: FlutterMediaComposerContext): Promise<boolean> {
    const result = await this.flutterShell.presentMediaComposer(start);
    if (!result) return false;

    let clientJobId: string | null = null;
    try {
      const localThumbnailUri = result.mediaType === 'image'
        ? this.toPreviewUrl(result.mediaUrl)
        : this.toPreviewUrl(result.thumbnailUrl);
      const optimistic = this.publisher.beginNativePublish({
        caption: result.caption || '',
        projectVM: context.projectVM,
        eventAM: context.eventAM,
        currentUser: context.currentUser,
        timeline: context.timeline,
        localThumbnailUri,
      });
      clientJobId = this.createClientJobId();
      this.nativeJobs.set(clientJobId, {optimistic, context, caption: result.caption || ''});
      const job = await NativeMediaPublisher.enqueue({
        clientJobId,
        mediaPath: result.mediaPath,
        thumbnailPath: result.thumbnailPath,
        mediaType: result.mediaType,
        mimeType: result.mimeType,
        thumbnailTime: result.thumbnailTime ?? null,
        filterPreset: result.filterPreset ?? null,
        apiUrl: environment.apiUrl,
      });
      // Native code keeps the supplied id. Retain this guard for a stale app
      // build so an event from the returned id is still correlated safely.
      if (job.jobId !== clientJobId) {
        const pending = this.nativeJobs.get(clientJobId);
        this.nativeJobs.delete(clientJobId);
        if (pending) this.nativeJobs.set(job.jobId, pending);
      }
      return true;
    } catch (error) {
      console.error('[FlutterMediaComposer] unable to enqueue native result', error);
      const pending = clientJobId ? this.nativeJobs.get(clientJobId) : undefined;
      if (pending) {
        this.nativeJobs.delete(clientJobId!);
        this.publisher.failNativePublish(pending.optimistic, {
          caption: pending.caption,
          projectVM: pending.context.projectVM,
          eventAM: pending.context.eventAM,
          currentUser: pending.context.currentUser,
          timeline: pending.context.timeline,
        });
      } else {
        this.notifications.emitError(this.translate.instant('shared.modal.component.add_media.error.upload_failed'));
      }
      return false;
    }
  }

  private handleNativeJob(event: NativeMediaPublishEvent): void {
    const job = this.nativeJobs.get(event.jobId);
    if (!job) return;
    if (event.state === 'uploaded' && event.mediaIri) {
      this.nativeJobs.delete(event.jobId);
      this.publisher.completeNativePublish(job.optimistic, event.mediaIri, {
        caption: job.caption,
        projectVM: job.context.projectVM,
        eventAM: job.context.eventAM,
        currentUser: job.context.currentUser,
        timeline: job.context.timeline,
      });
    } else if (event.state === 'failed') {
      this.nativeJobs.delete(event.jobId);
      this.publisher.failNativePublish(job.optimistic, {
        caption: job.caption,
        projectVM: job.context.projectVM,
        eventAM: job.context.eventAM,
        currentUser: job.context.currentUser,
        timeline: job.context.timeline,
      });
    }
  }

  private toPreviewUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    return url.startsWith('capacitor://localhost/')
      ? url.replace('capacitor://localhost/', 'http://localhost/')
      : url;
  }

  private createClientJobId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `native-media-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
